// CountdownDial — the per-task clock face shown while a task is active
// (DECISIONS §E.2, README §6.7.3 item 3).
//
// Purely presentational, like TimerDisplay: it is handed a
// `durationSeconds` and a `remainingSeconds` and draws an SVG ring that
// empties as the task's window closes. The cooking pane feeds it from
// the `useCountdown` hook. Keeping the time arithmetic out of this
// component makes it trivially testable with fixed numbers.
//
// Motion: the foreground arc carries the `cc-dial-arc` class, which
// gives it a 1 s linear `stroke-dashoffset` transition so the
// once-per-second updates sweep smoothly rather than jump. Under
// `prefers-reduced-motion` (or the design-lab toggle) styles.css strips
// the transition, leaving a static arc plus the numeric remaining time.

import { theme } from '../theme';

export interface CountdownDialProps {
  /** Whole task window in seconds (`end − start`). */
  durationSeconds: number;
  /** Seconds remaining, clamped 0..durationSeconds by the caller. */
  remainingSeconds: number;
  /** Pixel diameter. Default 48. */
  size?: number;
  /** Arc colour. Default terracotta (theme.accent.action). */
  color?: string;
  /** Track colour — the circle revealed as the arc peels back. Default
   *  oat (theme.bg.muted); the active-task clock passes white so the
   *  countdown reads as a filled ring emptying onto a clean disc. */
  trackColor?: string;
  /** Optional accessible-name prefix, e.g. the task title. */
  label?: string;
}

function formatRemaining(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  // Compact for the small dial face: H:MM when over an hour, else M:SS.
  return h > 0 ? `${h}:${pad(m)}` : `${pad(m)}:${pad(s)}`;
}

function formatSpoken(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  const parts: string[] = [];
  if (m > 0) parts.push(`${m} minute${m === 1 ? '' : 's'}`);
  parts.push(`${s} second${s === 1 ? '' : 's'}`);
  return parts.join(' ');
}

export function CountdownDial({
  durationSeconds,
  remainingSeconds,
  size = 48,
  color = theme.accent.action,
  trackColor = theme.bg.muted,
  label,
}: CountdownDialProps) {
  const remaining = Math.max(0, Math.min(remainingSeconds, durationSeconds || remainingSeconds));
  const fraction = durationSeconds > 0 ? Math.max(0, Math.min(1, remaining / durationSeconds)) : 0;

  const strokeWidth = Math.max(3, Math.round(size * 0.1));
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // dashoffset 0 → full ring; circumference → empty ring.
  const dashoffset = circumference * (1 - fraction);

  const ariaLabel = `${label ? `${label} ` : ''}countdown, ${formatSpoken(remaining)} remaining`;

  return (
    <div
      role="timer"
      aria-label={ariaLabel}
      data-cc-countdown-dial=""
      data-remaining-seconds={remaining}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        position: 'relative',
        flex: '0 0 auto',
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        {/* Track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {/* Remaining arc — sweeps anticlockwise from 12 o'clock as it empties. */}
        <circle
          className="cc-dial-arc"
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashoffset}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `${Math.max(0.55, size / 90)}rem`,
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: theme.text.primary,
          letterSpacing: '0.02em',
        }}
      >
        {formatRemaining(remaining)}
      </span>
    </div>
  );
}

export default CountdownDial;
